/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import {
    AdminConfiguration,
    CustomComplianceSettings,
    HvacComplianceSettings,
    SalesFeatureSettings,
} from '@centecomm/admin-config-service';
import { createSelector } from 'reselect';
import { State } from 'Types';

const SettingsState = ({ settings }: State) => settings;

// Data slices
const communicationSlice = createSelector(SettingsState, ({ data }) => data?.communication);
const companySlice = createSelector(SettingsState, ({ data }) => (data as AdminConfiguration)?.company);
const companyIdSlice = createSelector(SettingsState, ({ data }) => data?.companyId);
const customComplianceSlice = createSelector(SettingsState, ({ data }) => data?.customCompliance);
const documentManagementSlice = createSelector(SettingsState, ({ data }) => data?.documentManagement);
const helpGuidesSlice = createSelector(SettingsState, ({ data }) => data?.helpGuides);
const hvacComplianceSlice = createSelector(SettingsState, ({ data }) => data?.hvacCompliance);
const idPlansIntegrationSlice = createSelector(SettingsState, ({ data }) => data?.idPlansIntegration);
const inspectionSlice = createSelector(SettingsState, ({ data }) => data?.inspection);
const insuranceSlice = createSelector(SettingsState, ({ data }) => data?.insurance);
const invoiceSlice = createSelector(SettingsState, ({ data }) => data?.invoice);
const leasingSlice = createSelector(SettingsState, ({ data }) => data?.leasing);
const landingPageSlice = createSelector(SettingsState, ({ data }) => data?.landingPage);
const locationSlice = createSelector(SettingsState, ({ data }) => data?.location);
const reportSlice = createSelector(SettingsState, ({ data }) => data?.report);
const requestSlice = createSelector(SettingsState, ({ data }) => data?.request);
const saleSlice = createSelector(SettingsState, ({ data }) => data?.sale);
const welcomeSlice = createSelector(SettingsState, ({ data }) => data?.welcome);

// Load status
export const settingsLoadStatus = createSelector(SettingsState, ({ loadStatus }) => loadStatus);

// Data
export const communicationSettings = createSelector(communicationSlice, (_) => _);
export const companySettings = createSelector(companySlice, (_) => _);
export const companyId = createSelector(companyIdSlice, (_) => _);
export const customComplianceSettings = createSelector(customComplianceSlice, (_) => _);
export const documentManagementSettings = createSelector(documentManagementSlice, (_) => _);
export const helpGuidesSettings = createSelector(helpGuidesSlice, (_) => _);
export const hvacComplianceSettings = createSelector(hvacComplianceSlice, (_) => _);
export const idPlansIntegrationSettings = createSelector(idPlansIntegrationSlice, (_) => _);
export const inspectionSettings = createSelector(inspectionSlice, (_) => _);
export const insuranceSettings = createSelector(insuranceSlice, (_) => _);
export const invoiceSettings = createSelector(invoiceSlice, (_) => _);
export const landingPageSettings = createSelector(landingPageSlice, (_) => _);
export const leasingSettings = createSelector(leasingSlice, (_) => _);
export const locationSettings = createSelector(locationSlice, (_) => _);
export const reportSettings = createSelector(reportSlice, (_) => _);
export const requestSettings = createSelector(requestSlice, (_) => _);
export const saleSettings = createSelector(saleSlice, (_) => _);
export const welcomeSettings = createSelector(welcomeSlice, (_) => _);

// HVAC Compliance stuff
export const findHvacComplianceSettingsForOccupant = (occupantId: number) =>
    createSelector(hvacComplianceSettings, (settings: HvacComplianceSettings) => settings?.occupantsSettings?.[occupantId]);

// Custom Compliance (Compliance Builder). Nested one level deeper than the others:
// - per definition.
// - then per occupant.
export const findCustomComplianceSettingsForOccupant = (definitionId: number, occupantId: number) =>
    createSelector(
        customComplianceSettings,
        (settings: CustomComplianceSettings) => settings?.compliances?.[definitionId]?.occupantsSettings?.[occupantId],
    );

/**
 * How many occupants a definition is currently turned on for, used by the Compliance Builder landing page.
 */
export const countEnabledOccupantsForCustomCompliance = (definitionId: number) =>
    createSelector(
        customComplianceSettings,
        (settings: CustomComplianceSettings) =>
            Object.values(settings?.compliances?.[definitionId]?.occupantsSettings ?? {}).filter((_) => _.enabled).length,
    );

export const countEnabledOccupantsForHvacCompliance = createSelector(
    hvacComplianceSettings,
    (settings: HvacComplianceSettings) => Object.values(settings?.occupantsSettings ?? {}).filter((_) => _.enabled).length,
);

export const countEnabledOccupantsForSalesCompliance = createSelector(
    saleSettings,
    (settings: SalesFeatureSettings) => Object.values(settings?.occupantsSettings ?? {}).filter((_) => _.enabled).length,
);

// Sales Compliance
export const findSalesSettingsForOccupant = (occupantId: number) =>
    createSelector(saleSettings, (settings: SalesFeatureSettings) => settings?.occupantsSettings?.[occupantId]);
